import { unravel } from '@/service/jsonApi'
import { API_BASE_URL } from '@/environment'
import { customFetch } from '@/service/v3/apiV3Utils'
import { queryParams } from '@attensi/utils'
import { apiV3Admin } from '@/service/v3/apiV3'
import ApiService from '@/service/api.service.js'
import type {
  GetDeleteAiAssistantFromPolicyParams,
  GetCompanyPoliciesParams,
  GetDeleteProductFromPolicyParams,
  GetDeleteScenarioFromPolicyParams,
  GetPatchProductPolicyParams,
  GetPatchScenarioPolicyParams,
  GetPostProductToPolicyParams,
  GetPostScenarioToPolicyParams,
  GetPostAiAssistantToPolicyParams,
} from '@/service/v3/endpoints/policiesParams'
import type { AiAssistant, AiAssistantsListResponse } from '@/types/aiAssistant'

export const getCompanyPolicies = async ({
  companyNameKey,
  filterTerm,
  pageNumber = 1,
  pageSize = 20,
  sort = 'name',
}: GetCompanyPoliciesParams) => {
  const policyQueryParams = {
    'filter[term]': filterTerm,
    'page[number]': pageNumber,
    'page[size]': pageSize,
    sort,
  }
  const url = queryParams(
    `${API_BASE_URL}/api/v3/admin/companies/${companyNameKey}/policies`,
    policyQueryParams
  )

  return await customFetch(url, {
    method: 'GET',
  }).then((response) => response.json())
}

export const getPolicyById = async (policyId: string) => {
  const url = `${API_BASE_URL}/api/v3/admin/policies/${policyId}`
  return await customFetch(url, {
    method: 'GET',
  }).then((response) => response.json())
}

// Products:
export function postProductToPolicy({
  policyId,
  productId,
  policyState,
}: GetPostProductToPolicyParams) {
  // TODO: use this v3 endpoint when policy state is fixed (waiting for backend):
  /* return apiV3Admin.policies.adminLinkPolicyProduct(policyId, {
    data: {
      id: policyId,
      type: 'product_policy',
      attributes: {
        product_id: productId,
        state: policyState,
      },
    },
  }) */
  return ApiService.addProductToPolicies(productId, policyId, policyState)
}

export function patchProductPolicy({
  policyId,
  productId,
  policyState,
}: GetPatchProductPolicyParams) {
  return apiV3Admin.policies.adminUpdatePolicyProduct(policyId, productId, {
    data: {
      id: String(policyId),
      type: 'product_policy',
      attributes: {
        product_id: String(productId),
        state: policyState,
      },
    },
  })
}

export function deleteProductFromPolicy({
  policyId,
  productId,
}: GetDeleteProductFromPolicyParams) {
  return apiV3Admin.policies.adminDeletePolicyProduct(policyId, productId)
}

// Scenarios:
export function postScenarioToPolicy({
  policyId,
  scenarioId,
  policyState,
}: GetPostScenarioToPolicyParams) {
  return apiV3Admin.policies.adminLinkPolicyScenario(policyId, {
    data: {
      id: policyId,
      type: 'policy_scenario',
      attributes: {
        scenario_id: scenarioId,
        state: policyState,
      },
    },
  })
}

export function patchScenarioPolicy({
  policyId,
  scenarioId,
  policyState,
}: GetPatchScenarioPolicyParams) {
  return apiV3Admin.policies.adminUpdatePolicyScenario(policyId, scenarioId, {
    data: {
      id: String(policyId),
      type: 'policy_scenario',
      attributes: {
        scenario_id: String(scenarioId),
        state: policyState,
      },
    },
  })
}

export function deleteScenarioFromPolicy({
  policyId,
  scenarioId,
}: GetDeleteScenarioFromPolicyParams) {
  return apiV3Admin.policies.adminDeletePolicyScenariot(policyId, scenarioId)
}

export const postAiAssistantToPolicy = async ({
  policyId,
  aiAssistantId,
}: GetPostAiAssistantToPolicyParams): Promise<void> => {
  const url = `${API_BASE_URL}/api/v3/admin/policies/${policyId}/ai_assistants`

  await customFetch(url, {
    method: 'POST',
    body: JSON.stringify({
      data: {
        attributes: {
          ai_assistant_id: aiAssistantId,
        },
      },
    }),
  })
}

export const deleteAiAssistantFromPolicy = async ({
  policyId,
  aiAssistantPolicyId,
}: GetDeleteAiAssistantFromPolicyParams): Promise<void> => {
  const url = `${API_BASE_URL}/api/v3/admin/policies/${policyId}/ai_assistants/${aiAssistantPolicyId}`

  await customFetch(url, { method: 'DELETE' })
}

export const getPolicyAiAssistants = async (
  policyId: string | undefined
): Promise<AiAssistant[] | undefined> => {
  if (policyId === undefined) return undefined

  const url = `${API_BASE_URL}/api/v3/admin/policies/${policyId}/ai_assistants`

  const response = await customFetch(url, { method: 'GET' })
  const json = (await response.json()) as AiAssistantsListResponse

  return unravel(json) as AiAssistant[]
}
